import { IMeasurementMetadata } from "@tberrysoln/rsf-visualizer";
import { ActionsUnion, createAction } from "common/reducers/UtilsTypes";
import { IAnnotationData } from "common/records/Annotation";
import { ElevationRecord, IElevationData, IElevationRecord } from "common/records/Elevation";

export const RECEIVE_ELEVATION = "@elevations/RECEIVE_ELEVATION";
export const RESTORE_ELEVATION = "@elevations/RESTORE_ELEVATION";
export const RESET_ELEVATION = "@elevations/RESET_ELEVATION";
export const CLEAR_ELEVATION = "@elevations/CLEAR_ELEVATION";
export const SNAPSHOT_ELEVATION = "@elevations/SNAPSHOT_ELEVATION";
export const CLEAR_ELEVATION_SNAPSHOTS = "@elevations/CLEAR_ELEVATION_SNAPSHOTS";
export const UPDATE_ANNOTATIONS = "@elevations/UPDATE_ANNOTATIONS";
export const UPDATE_ELEVATION_FIELD = "@elevations/UPDATE_ELEVATION_FIELD";
export const ADD_ANNOTATION = "@elevations/ADD_ANNOTATION";
export const MOVE_ANNOTATION = "@elevations/MOVE_ANNOTATION";
export const REMOVE_ANNOTATION = "@elevations/REMOVE_ANNOTATION";
export const SET_MEASURING_METADATA = "@elevations/SET_MEASURING_METADATA";

export interface AnnotationTarget {
  elevationId: number;
  guid: string;
}

export interface UpdateAnnotationsEvent {
  targets: AnnotationTarget[];
  field: string;
  value: any;
}

export const ElevationActions = {
  receiveElevation: (elevation: Partial<IElevationData>) => createAction(RECEIVE_ELEVATION, { elevation }),
  /**
   * Writes an already-hydrated record into `byId` ONLY — unlike `receiveElevation`, which also
   * moves the `lastSavedById` server baseline. Used by the save pipeline's failure path to put the
   * captured (unsaved) records back after the mid-save re-hydration clobbered `byId`, so the
   * wizard stays dirty and a retry re-sends the user's edits.
   */
  restoreElevation: (elevation: ElevationRecord) => createAction(RESTORE_ELEVATION, { elevation }),
  resetElevation: (elevationId: number) => createAction(RESET_ELEVATION, { elevationId }),
  /**
   * Deletes the elevation from byId, lastSavedById, AND the editing snapshot — used when the
   * wizard drops a never-saved placeholder elevation. `resetElevation` only deletes byId when
   * neither a checkpoint nor a server baseline exists; once Step 1's "Choose & Add Labels" has
   * seeded a slot, dropping requires the harder clear semantics.
   */
  clearElevation: (elevationId: number) => createAction(CLEAR_ELEVATION, { elevationId }),
  /**
   * Per-step "Save Elevation" checkpoint: copies the elevation's current `byId` state into the
   * in-wizard `editingSnapshotById` so a later Cancel rolls back to it — without touching
   * `lastSavedById` (the server baseline the overall Save diffs against).
   */
  snapshotElevation: (elevationId: number) => createAction(SNAPSHOT_ELEVATION, { elevationId }),
  /** Clears all in-wizard checkpoints; dispatched on modal close so the next session starts fresh. */
  clearElevationSnapshots: () => createAction(CLEAR_ELEVATION_SNAPSHOTS),
  updateAnnotations: (event: UpdateAnnotationsEvent) => createAction(UPDATE_ANNOTATIONS, { event }),
  updateElevationField: (elevationId: number, field: keyof IElevationRecord, value: any) =>
    createAction(UPDATE_ELEVATION_FIELD, { elevationId, field, value }),
  addAnnotation: (elevationId: number, annotation: Partial<IAnnotationData>) =>
    createAction(ADD_ANNOTATION, { elevationId, annotation }),
  moveAnnotation: (elevationId: number, guid: string, x: number, y: number) =>
    createAction(MOVE_ANNOTATION, { elevationId, guid, x, y }),
  /**
   * Moves the annotation into `removedAnnotations` rather than dropping it outright, so the Save
   * flow can drain any annotation that already had a server `opening_id` into `_destroy` on the
   * matching Window/Door (see `mergeElevationsIntoOpenings` in `saveElevations`).
   */
  removeAnnotation: (elevationId: number, guid: string) => createAction(REMOVE_ANNOTATION, { elevationId, guid }),
  /**
   * Stores the serialized canvas state from `RsfDrawing.serialize()` on the elevation. The
   * elevation save body packs `annotations: { list, wallFacades, measuring }`, so the canonical
   * storage location is `annotations.measuring` — not a top-level `drawingMetadata` field (which
   * doesn't exist on the Record despite the AC wording).
   */
  setMeasuringMetadata: (elevationId: number, metadata: IMeasurementMetadata) =>
    createAction(SET_MEASURING_METADATA, { elevationId, metadata }),
};

export type ElevationActions = ActionsUnion<typeof ElevationActions>;
